import { useCallback, useState } from 'react'
import { useCachedUserProfileState, useCachedContentQuery } from 'PublicWebApi'

import { Avatar, Typography } from '@mui/material'
import ArrowDropDownRounded from '@mui/icons-material/ArrowDropDownRounded'
import ArrowDropUpRounded from '@mui/icons-material/ArrowDropUpRounded'

import { useSliceStateSelector } from 'slices/hooks'
import AvatarPopover from '../AvatarPopover'
import { StyledSecondaryActionButton } from 'App/Header/Header.styles'

interface LoggedInAvatarButtonProps {
  compact?: boolean
  withBorder?: boolean
}

const LoggedInAvatarButton = ({
  compact,
  withBorder,
}: LoggedInAvatarButtonProps) => {
  const { preferredFirstName, profileImages, imageFileName } =
    useCachedUserProfileState()
  const { data: contentData } = useCachedContentQuery()

  const { isMobile, isTablet } = useSliceStateSelector(
    (state) => state.slices.device.deviceSize
  )
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)

  const openPopover = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const isPublicPage = contentData?.properties.isPublic

  const isMobileOrTablet = isMobile || isTablet
  const firstLetter = preferredFirstName?.slice(0, 1)

  const showPreferredFirstName = !isMobileOrTablet

  const renderWithBorder = withBorder || !isPublicPage

  const showArrowDown = !isMobileOrTablet

  const toggleArrow = useCallback(() => {
    if (anchorEl) {
      return <ArrowDropUpRounded color="primary" />
    }
    return <ArrowDropDownRounded color="primary" />
  }, [anchorEl])

  return (
    <>
      <StyledSecondaryActionButton
        compact={compact}
        withBorder={withBorder}
        data-testid="loggedin-avatar-wrapper"
        onClick={openPopover}
        disableRipple
        data-gtm-label="username"
      >
        <Avatar
          sx={{ height: 24, width: 24, bgcolor: 'primary.main' }}
          {...(imageFileName && {
            src: profileImages?.small,
            alt: 'Profilbild',
          })}
          data-testid="loggedin-avatar"
        >
          <Typography variant="subtitle2">{firstLetter}</Typography>
        </Avatar>
        {showPreferredFirstName && (
          <Typography
            variant="body1"
            color={renderWithBorder ? 'primary.main' : 'text.primary'}
          >
            {preferredFirstName}
          </Typography>
        )}
        {showArrowDown && toggleArrow()}
      </StyledSecondaryActionButton>

      <AvatarPopover
        isPublicPage={isPublicPage}
        firstName={preferredFirstName}
        anchorEl={anchorEl}
        setAnchorEl={setAnchorEl}
      />
    </>
  )
}

export default LoggedInAvatarButton
